import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Image, Trash2, Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Placeholder for uploaded images (would come from API)
const mockUploads = [
  { id: '1', url: 'https://picsum.photos/seed/upload1/400/300', name: 'Product photo.jpg', uploadedAt: Date.now() - 86400000, size: '2.4 MB' },
  { id: '2', url: 'https://picsum.photos/seed/upload2/400/300', name: 'Team meeting.png', uploadedAt: Date.now() - 172800000, size: '1.8 MB' },
  { id: '3', url: 'https://picsum.photos/seed/upload3/400/300', name: 'Office space.jpg', uploadedAt: Date.now() - 259200000, size: '3.1 MB' },
  { id: '4', url: 'https://picsum.photos/seed/upload4/400/300', name: 'Logo design.png', uploadedAt: Date.now() - 345600000, size: '0.8 MB' },
  { id: '5', url: 'https://picsum.photos/seed/upload5/400/300', name: 'Background.jpg', uploadedAt: Date.now() - 432000000, size: '2.1 MB' },
  { id: '6', url: 'https://picsum.photos/seed/upload6/400/300', name: 'Screenshot.png', uploadedAt: Date.now() - 518400000, size: '1.2 MB' },
]

export default function UploadsPage() {
  const [uploads] = useState(mockUploads)
  const [selectedUploads, setSelectedUploads] = useState<string[]>([])
  const [isLoading] = useState(false)

  const toggleSelect = (id: string) => {
    setSelectedUploads((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    )
  }

  return (
    <div className="p-6">
      <Helmet>
        <title>Uploads - Footage</title>
        <meta name="description" content="Manage your uploaded files." />
      </Helmet>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Uploads</h1>
            <p className="text-sm text-neutral-500 mt-1">
              Images you've uploaded in your video projects.
            </p>
          </div>
          {selectedUploads.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">
                {selectedUploads.length} selected
              </span>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {uploads.length === 0 ? (
          <div className="border-2 border-dashed border-neutral-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
              <Image className="h-8 w-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-1">No uploads yet</h3>
            <p className="text-sm text-neutral-500 max-w-sm mx-auto">
              Images you upload while creating videos will appear here for easy reuse.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {uploads.map((upload) => (
              <div
                key={upload.id}
                onClick={() => toggleSelect(upload.id)}
                className={cn(
                  'group relative aspect-4/3 rounded-xl overflow-hidden border-2 cursor-pointer transition-all',
                  selectedUploads.includes(upload.id)
                    ? 'border-teal-500 ring-2 ring-teal-500/20'
                    : 'border-neutral-200 hover:border-neutral-300'
                )}
              >
                <img
                  src={upload.url}
                  alt={upload.name}
                  className="w-full h-full object-cover"
                />

                {/* Selection overlay */}
                <div
                  className={cn(
                    'absolute inset-0 transition-opacity',
                    selectedUploads.includes(upload.id)
                      ? 'bg-teal-500/10'
                      : 'bg-black/0 group-hover:bg-black/10'
                  )}
                />

                {/* Checkbox */}
                <div
                  className={cn(
                    'absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-all',
                    selectedUploads.includes(upload.id)
                      ? 'bg-teal-500 border-teal-500'
                      : 'bg-white/80 border-neutral-300 opacity-0 group-hover:opacity-100'
                  )}
                >
                  {selectedUploads.includes(upload.id) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>

                {/* Info overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent p-3">
                  <p className="text-sm text-white font-medium truncate">{upload.name}</p>
                  <p className="text-xs text-white/70">{formatDate(upload.uploadedAt)} · {upload.size}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
